// Typed API client for the bubble-wu-auditor backend.
// Contracts mirror the B-API chunk handler JSON shapes (P14 read-API).
// All fetch calls use apiFetch (credentials:'include', non-2xx → ApiError).

import { apiFetch } from "@avagency/frontend/lib/api";

// RFC 4122 UUID shape — same pattern as the backend uuidRE guard.
const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function assertUUID(value: string, label: string): void {
  if (!UUID_RE.test(value)) {
    throw new Error(`${label} is not a valid UUID: ${value}`);
  }
}

// ──────────────────────── Integrations ────────────────────────

export interface Integration {
  id: string;
  provider: string; // "bubble"
  display_name: string;
  status: string; // "active" | "revoked" | "error" | "reauth_required"
}

export async function fetchIntegrations(): Promise<Integration[]> {
  return apiFetch<Integration[]>("/api/v1/integrations");
}

// ──────────────────────── Apps ────────────────────────

export interface App {
  id: string;
  integration_id: string;
  app_name: string;
  app_slug: string;
  last_wu_pct: number | null; // null until first audit
  last_audited_at: string | null; // RFC 3339 or null
}

export async function fetchApps(integrationId?: string): Promise<App[]> {
  const params = new URLSearchParams();
  if (integrationId) {
    assertUUID(integrationId, "integrationId");
    params.set("integration_id", integrationId);
  }
  const qs = params.size ? `?${params.toString()}` : "";
  return apiFetch<App[]>(`/api/v1/apps${qs}`);
}

// ──────────────────────── Audits ────────────────────────

export interface Audit {
  id: string;
  app_id: string;
  integration_id: string;
  started_at: string; // RFC 3339
  finished_at: string | null;
  status: string; // "ready_to_run" | "running" | "complete" | "failed"
  projected_wu_pct: number | null; // null until complete
  failure_reason?: string;
}

export async function fetchAudits(appId: string): Promise<Audit[]> {
  assertUUID(appId, "appId");
  const params = new URLSearchParams({ app_id: appId });
  return apiFetch<Audit[]>(`/api/v1/audits?${params.toString()}`);
}

// ──────────────────────── Dashboard ────────────────────────

export interface DashboardData {
  total_apps: number;
  audits_last_30d: number;
  apps_at_risk: number; // last_wu_pct >= 100
}

export async function fetchDashboard(): Promise<DashboardData> {
  return apiFetch<DashboardData>("/api/v1/dashboard");
}

// ──────────────────────── Connect ────────────────────────

export interface ConnectBubblePayload {
  api_key: string;
  app_slug: string;
  app_id: string;
}

export async function connectBubble(payload: ConnectBubblePayload): Promise<void> {
  await apiFetch<unknown>("/api/v1/integrations/bubble/connect", {
    method: "POST",
    json: payload,
  });
}
